import { UniqueIdentifier } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';

export type SharedFileWithOrder = {
  id: UniqueIdentifier;
  order?: number;
};

export const getSharedFileOrder = (sharedFile: SharedFileWithOrder, fallbackOrder: number) => (
  sharedFile.order ?? fallbackOrder
);

export const getOrderedSharedFiles = <T extends SharedFileWithOrder>(sharedFiles: T[]): T[] => {
  return sharedFiles
    .map((sharedFile, index) => ({ sharedFile, index }))
    .sort((a, b) => (
      getSharedFileOrder(a.sharedFile, a.index) - getSharedFileOrder(b.sharedFile, b.index)
    ))
    .map(({ sharedFile }) => sharedFile);
};

export const withSequentialSharedFileOrder = <T extends SharedFileWithOrder>(sharedFiles: T[]): T[] => (
  sharedFiles.map((sharedFile, index) => ({
    ...sharedFile,
    order: index,
  }))
);

export const reorderSharedFiles = <T extends SharedFileWithOrder>(
  sharedFiles: T[],
  activeId: UniqueIdentifier,
  overId?: UniqueIdentifier
): T[] => {
  if (!overId || activeId === overId || sharedFiles.length <= 1) return sharedFiles;

  const oldIndex = sharedFiles.findIndex(sharedFile => sharedFile.id === activeId);
  const newIndex = sharedFiles.findIndex(sharedFile => sharedFile.id === overId);

  if (oldIndex === -1 || newIndex === -1) return sharedFiles;

  return withSequentialSharedFileOrder(arrayMove(sharedFiles, oldIndex, newIndex));
};
